// src/pages/Feedback.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'question', label: 'Question' },
  { value: 'compliment', label: 'Compliment' },
  { value: 'other', label: 'Other' },
];

const STATUS_COLORS = {
  new: 'info',
  reviewed: 'warning',
  resolved: 'success',
  archived: 'default',
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 3;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const Feedback = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);

  // Submit tab state
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // History tab state
  const [feedbackList, setFeedbackList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      const res = await userAPI.getMyFeedback({ page, limit: 10 });
      setFeedbackList(res.data.feedback);
      setPagination(res.data.pagination);
    } catch (err) {
      setHistoryError('Failed to load feedback history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (tab === 1) {
      loadHistory();
    }
  }, [tab, loadHistory]);

  const addFiles = (selected) => {
    const validFiles = [];
    const errors = [];

    for (const file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File too large (max 5MB)`);
        continue;
      }
      validFiles.push(file);
    }

    const totalFiles = files.length + validFiles.length;
    if (totalFiles > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files allowed`);
      validFiles.splice(MAX_FILES - files.length);
    }

    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!type) {
      setError('Please select a feedback type.');
      return;
    }
    if (!message.trim()) {
      setError('Please enter a message.');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('type', type);
      formData.append('message', message.trim());
      files.forEach(file => formData.append('attachments', file));

      await userAPI.submitFeedback(formData);

      // Reset form
      setType('');
      setMessage('');
      setFiles([]);
      setSuccessMessage(true);

      // Switch to history tab
      setTab(1);
      setPage(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttachmentClick = async (feedbackId, attachment) => {
    try {
      const res = await userAPI.getFeedbackAttachmentUrl(feedbackId, attachment.key);
      window.open(res.data.url, '_blank');
    } catch (err) {
      setHistoryError('Failed to load attachment.');
    }
  };

  const isImageType = (contentType) => contentType && contentType.startsWith('image/');

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Feedback</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Help us improve Second Clear. We read every piece of feedback.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Submit Feedback" />
        <Tab label="My Feedback" />
      </Tabs>

      {/* Tab 1: Submit Feedback */}
      {tab === 0 && (
        <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              select
              label="Feedback Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            >
              {FEEDBACK_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Your Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              rows={6}
              fullWidth
              placeholder="Tell us what's on your mind..."
              inputProps={{ maxLength: 5000 }}
              helperText={`${message.length}/5000`}
              sx={{ mb: 3 }}
            />

            {/* File Attachments — click or drag & drop */}
            <Box sx={{ mb: 3 }}>
              <input
                type="file"
                id="feedback-attachments"
                multiple
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Box
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => {
                  if (files.length < MAX_FILES) {
                    document.getElementById('feedback-attachments').click();
                  }
                }}
                sx={{
                  border: '2px dashed',
                  borderColor: dragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  cursor: files.length >= MAX_FILES ? 'default' : 'pointer',
                  bgcolor: dragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': files.length < MAX_FILES ? {
                    borderColor: 'primary.light',
                    bgcolor: 'rgba(25, 118, 210, 0.02)',
                  } : {},
                }}
              >
                <UploadIcon color={dragActive ? 'primary' : 'action'} sx={{ fontSize: 32, mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {files.length >= MAX_FILES
                    ? `Maximum ${MAX_FILES} files attached`
                    : 'Drag & drop files here, or click to browse'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  JPEG, PNG, GIF, WebP, PDF — max 5MB each, up to {MAX_FILES} files
                </Typography>
              </Box>

              {files.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {files.map((file, index) => (
                    <Chip
                      key={index}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => removeFile(index)}
                      icon={file.type.startsWith('image/') ? <ImageIcon /> : <FileIcon />}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>

            <TextField
              label="Your Email"
              value={user?.email || ''}
              fullWidth
              disabled
              sx={{ mb: 3 }}
              helperText="Auto-filled from your account"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </form>
        </Paper>
      )}

      {/* Tab 2: My Feedback */}
      {tab === 1 && (
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          {historyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError('')}>
              {historyError}
            </Alert>
          )}

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : feedbackList.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No feedback submitted yet.</Typography>
            </Paper>
          ) : (
            <>
              {feedbackList.map((item) => (
                <Card key={item.id} sx={{ mb: 2 }}>
                  <CardContent>
                    {/* Header: type + status + date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={FEEDBACK_TYPES.find(t => t.value === item.type)?.label || item.type}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={item.status}
                        size="small"
                        color={STATUS_COLORS[item.status] || 'default'}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString()}
                      </Typography>
                    </Box>

                    {/* Message */}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                      {item.message}
                    </Typography>

                    {/* Attachments */}
                    {item.attachments && item.attachments.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {item.attachments.map((att, idx) => (
                          <Chip
                            key={idx}
                            label={att.filename}
                            size="small"
                            icon={isImageType(att.content_type) ? <ImageIcon /> : <FileIcon />}
                            onClick={() => handleAttachmentClick(item.id, att)}
                            clickable
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}

                    {/* Admin Response — show if there's a text response OR attachments */}
                    {(item.admin_response || item.admin_attachments?.length > 0) && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Response from Second Clear
                        </Typography>
                        {item.responded_at && (
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            {new Date(item.responded_at).toLocaleDateString()}
                          </Typography>
                        )}
                        {item.admin_response && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {item.admin_response}
                          </Typography>
                        )}

                        {/* Admin attachments */}
                        {item.admin_attachments?.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: item.admin_response ? 1.5 : 0 }}>
                            {item.admin_attachments.map((att, idx) => (
                              <Chip
                                key={idx}
                                label={att.filename}
                                size="small"
                                icon={isImageType(att.content_type) ? <ImageIcon /> : <FileIcon />}
                                onClick={() => handleAttachmentClick(item.id, att)}
                                clickable
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                  <IconButton
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    size="small"
                  >
                    <PrevIcon />
                  </IconButton>
                  <Typography variant="body2">
                    Page {pagination.page} of {pagination.pages}
                  </Typography>
                  <IconButton
                    onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={page >= pagination.pages}
                    size="small"
                  >
                    <NextIcon />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mt: 2, mb: 2 }}
          onClose={() => setSuccessMessage(false)}
        >
          We've received your message! In the meantime, check out our{' '}
          <a href="https://secondclear.com/faq" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 600 }}>
            FAQ
          </a>{' '}
          for quick answers.
        </Alert>
      )}
    </Box>
  );
};

export default Feedback;
